import React from 'react';
import { useSelector } from 'react-redux';
import find from 'ramda/src/find';
import isEmpty from 'ramda/src/isEmpty';
import propEq from 'ramda/src/propEq';
import values from 'ramda/src/values';
import cx from 'classnames';
import Avatar from '@components/@core/Avatar';
import Pagination from '@components/@core/Pagination';
import Rating from '@components/@core/Rating';
import EditDropdown from '@components/EditDropdown';
import Icon from '@components/@core/Icon';
import { ScorecardsMenu } from '~/services/config';
import { formatDate, scorecards } from '~/services/util';

export const ListHeader = ({ data = {}, onSort }) => {
    const order = data?.order ?? 'name';
    const sort = data?.sort ?? 'ASC';
    const isNameActive = order === 'name';
    const isScorecardActive = order === 'scorecard';
    const isActualsActive = order === 'actuals';
    const isStatusActive = order === 'status';
    const isScoreActive = order === 'score';
    const isDateActive = order === 'date';
    const isDesc = sort === 'DESC';

    const handleSortData = (e) => {
        const payload = { order: e, sort: order !== e || isDesc ? 'ASC' : 'DESC' };
        if (onSort) onSort(payload);
    };

    return (
        <div className="vdmm-cp-header desktop-flex dsl-m12 py-2">
            <div className="d-flex-3 cursor-pointer" onClick={() => handleSortData('name')}>
                Employee
                {data?.order && (
                    <span className="ms-1 sort-item">
                        <Icon color={isNameActive ? '#376caf' : '#343f4b'} name={`fas fa-sort-${isNameActive && isDesc ? 'up' : 'down'}`} size={10} />
                    </span>
                )}
            </div>
            <div className="d-flex-4 ms-2 cursor-pointer" onClick={() => handleSortData('scorecard')}>
                Monthly Scorecard
                {data?.order && (
                    <span className="ms-1 sort-item">
                        <Icon color={isScorecardActive ? '#376caf' : '#343f4b'} name={`fas fa-sort-${isScorecardActive && isDesc ? 'up' : 'down'}`} size={10} />
                    </span>
                )}
            </div>
            <div className="d-flex-1 ms-2 cursor-pointer" onClick={() => handleSortData('actuals')}>
                Actuals
                {data?.order && (
                    <span className="ms-1 sort-item">
                        <Icon color={isActualsActive ? '#376caf' : '#343f4b'} name={`fas fa-sort-${isActualsActive && isDesc ? 'up' : 'down'}`} size={10} />
                    </span>
                )}
            </div>
            <div className="d-flex-2 ms-2 cursor-pointer" onClick={() => handleSortData('status')}>
                Status
                {data?.order && (
                    <span className="ms-1 sort-item">
                        <Icon color={isStatusActive ? '#376caf' : '#343f4b'} name={`fas fa-sort-${isStatusActive && isDesc ? 'up' : 'down'}`} size={10} />
                    </span>
                )}
            </div>
            <div className="d-flex-2 ms-2 cursor-pointer" onClick={() => handleSortData('score')}>
                Score
                {data?.order && (
                    <span className="ms-1 sort-item">
                        <Icon color={isScoreActive ? '#376caf' : '#343f4b'} name={`fas fa-sort-${isScoreActive && isDesc ? 'up' : 'down'}`} size={10} />
                    </span>
                )}
            </div>
            <div className="d-flex-2 ms-2 cursor-pointer" onClick={() => handleSortData('date')}>
                Date
                {data?.order && (
                    <span className="ms-1 sort-item">
                        <Icon color={isDateActive ? '#376caf' : '#343f4b'} name={`fas fa-sort-${isDateActive && isDesc ? 'up' : 'down'}`} size={10} />
                    </span>
                )}
            </div>
            <div className="edit" />
        </div>
    );
};

const MobileListHeader = () => (
    <div className="mobile-block me-4">
        <div className="d-flex-3">
            <span className="dsl-m14">Scorecard</span>
        </div>
        <div className="d-flex-1">
            <span className="dsl-m14">Actuals</span>
        </div>
        <div className="d-flex-3">
            <span className="dsl-m14">Status</span>
        </div>
        <div className="d-flex-3">
            <span className="dsl-m14">Score</span>
        </div>
        <div className="d-flex-2">
            <span className="dsl-m14">Date</span>
        </div>
    </div>
);

export const EmployeeItem = ({ item, dateStart, dateEnd, onReport, onRowClick, onSelectMenu, onScorecardDetail }) => {
    const users = useSelector((state) => state.app.users);
    const role = useSelector((state) => state.app.app_role_id);

    let options = [];
    let scorecard = null;
    if (item.scorecards) {
        if (!item.scorecards.length) {
            options = ScorecardsMenu[role].filter((e) => e !== 'Save Actuals' && e !== 'Save Daily Actuals' && e !== 'Start Review' && e !== 'Unassign Scorecard');
        } else {
            options = ScorecardsMenu[role].filter((e) => e !== 'Assign Scorecard');
            scorecard = item.scorecards[0];
        }
        if (item.performance?.status === 2) {
            options = options.filter((e) => e !== 'Save Actuals' && e !== 'Save Daily Actuals' && e !== 'Start Review');
        }
        if (item.scorecards.length === 0) {
            options = options.filter((e) => e !== 'Save Actuals' && e !== 'Save Daily Actuals' && e !== 'Preview Scorecard' && e !== 'Performance Report');
        }
    }
    const user = find(propEq('id', item?.user?.id), users);
    const firstName = item?.user?.profile?.first_name || item?.profile?.first_name;
    const lastName = item?.user?.profile?.last_name || item?.profile?.last_name;
    const userName = firstName + ' ' + lastName;
    const status = isEmpty(item.scorecards) ? 'Non Assigned' : item.performance?.status === 2 ? 'Completed' : 'Incomplete';

    return (
        <div className="emp-list d-flex ssm-column ai-center ai-sm-unset ssm-start py-3 border-ssm-bottom" datacy="empList">
            <div className="d-h-start d-flex-3 w-unset ssm-100 mb-ssm-2">
                <Avatar name={userName} url={item?.user?.avatar || user?.avatar || item?.user?.profile?.avatar} />
                <div className="dsl-b14 emp-list-name ps-3 cursor-pointer d-flex-1" onClick={onReport(item)}>
                    {userName}
                </div>
                <EditDropdown className="mobile-block ms-auto" options={options} onChange={onSelectMenu(item)} />
            </div>
            <div className="d-flex d-flex-4 ai-center ms-2 ms-sm-0 truncate-one cursor-pointer" onClick={onScorecardDetail(scorecard)}>
                <label className="mobile-screen dsl-m14 w-40">Scorecard</label>
                <span className={cx('dsl-b14 truncate-one', !scorecard && 'score-detail')}>
                    {item.performance?.status === 2
                        ? values(item.performance.data.scorecards)[0].title
                        : item?.scorecards && item?.scorecards.length > 0
                          ? item?.scorecards[0]?.title
                          : 'No Scorecard Assigned'}
                </span>
            </div>
            <div className="d-flex d-flex-1 ms-2 ms-sm-0 ssm-left" onClick={onRowClick(item, 'start review')}>
                <label className="mobile-screen dsl-m14 w-40">Actuals</label>
                <span className="dsl-b14">{scorecards(item, dateStart, dateEnd, 'actuals')}</span>
            </div>
            <div className="d-flex d-flex-2 ms-2 ms-sm-0" onClick={onRowClick(item, 'start review')}>
                <label className="mobile-screen dsl-m14 w-40">Status</label>
                <span className="dsl-b14">{status}</span>
            </div>
            <div className="d-flex d-flex-2 ms-2 ms-sm-0" onClick={onRowClick(item, 'start review')}>
                <label className="mobile-screen dsl-m14 w-40">Score</label>
                {isEmpty(item.scorecards) ? (
                    <span className="dsl-b14">Non Assigned</span>
                ) : item.performance?.status === 2 ? (
                    <>
                        {item.performance?.data?.completed_average_star_rating === 'N/A' ? (
                            <span className="dsl-b14">Na (Per Manager)</span>
                        ) : (
                            <div className="ms-n2">
                                <Rating score={item.performance?.data?.completed_average_star_rating} />
                            </div>
                        )}
                    </>
                ) : (
                    <span className="dsl-b14">Incomplete</span>
                )}
            </div>
            <div className="d-flex d-flex-2 ms-2 ms-sm-0 ssm-left" onClick={onRowClick(item, 'start review')}>
                <label className="mobile-screen dsl-m14 w-40">Date</label>
                <span className="dsl-b14">{isEmpty(item.scorecards) ? 'Non Assigned' : formatDate(item.performance?.completed_at, 'MMM DD, YY', 'Incomplete')}</span>
            </div>
            <div className="edit desktop-screen">{options.length > 0 && <EditDropdown options={options} onChange={onSelectMenu(item)} />}</div>
        </div>
    );
};

const EmployeeList = ({ data, dateStart, dateEnd, onRowClick, onReport, onSelectMenu, onScorecardDetail, onPagination, onPer, onSort }) => (
    <>
        <ListHeader data={data} onSort={onSort} />
        {data.users.map((item, index) => (
            <EmployeeItem
                key={index}
                item={item}
                dateStart={dateStart}
                dateEnd={dateEnd}
                onReport={onReport}
                onRowClick={onRowClick}
                onSelectMenu={onSelectMenu}
                onScorecardDetail={onScorecardDetail}
            />
        ))}
        <Pagination current={data.page} total={data.last_page} per={data.per_page} onChange={onPagination} onPer={onPer} />
    </>
);

export default EmployeeList;
